import axios from 'axios'
import i18n from '../translations/i18n'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

/**
 * Known (English) auth API error messages → translation keys, so users never see
 * the raw server text. Unknown messages fall back to the caller's key.
 */
const SERVER_ERROR_KEYS: Record<string, string> = {
  'Invalid email or password': 'auth.loginFailed',
  'Email is required': 'auth.emailRequired',
  'Failed to send reset email': 'auth.resetFailed',
  'Password must be at least 6 characters': 'auth.passwordMinLength',
  'Invalid or expired reset token': 'auth.invalidResetToken',
  'Invalid or expired invitation token': 'auth.invitationExpired',
  'Token and password are required': 'auth.fillAllFields',
  'Invalid email format': 'form.error.email',
  'Email already in use': 'register.emailInUse',
  'Validation failed': 'api.error.validation',
  'Internal server error': 'api.error.generic',
}

/**
 * Localized message for a failed auth API call
 * @param error - axios error
 * @param fallbackKey - translation key used when the server message is unknown/missing
 * @returns translated message in the current UI language
 */
export function translateAuthError(error: any, fallbackKey: string): string {
  const serverMsg: string | undefined = error?.response?.data?.error
  return i18n.t(SERVER_ERROR_KEYS[serverMsg || ''] || fallbackKey)
}

const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'
const CREDENTIALS_KEY = 'auth_credentials'

/**
 * Login credentials interface
 */
export interface LoginCredentials {
  email: string
  password: string
}

/**
 * Login response interface
 */
export interface LoginResponse {
  token: string
  user: {
    id: string
    name: string
    email: string
    role: 'super_admin' | 'admin' | 'user'
    isActive: boolean
    lastLogin?: string
    createdAt: string
    parentAccountId?: string
  }
  expiresIn: number
}

/**
 * Stores authentication token
 * @param token - JWT token
 * @returns void
 */
export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * Retrieves authentication token
 * @returns Token string or null
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * Removes authentication token
 * @returns void
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(CREDENTIALS_KEY)
}

/**
 * Stores user data
 * @param user - User object
 * @returns void
 */
export function storeUser(user: LoginResponse['user']): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

/**
 * Stores login credentials
 * @param email - User email
 * @param password - User password
 * @returns void
 */
export function storeCredentials(email: string, password: string): void {
  localStorage.setItem(CREDENTIALS_KEY, JSON.stringify({ email, password }))
}

/**
 * Retrieves stored credentials
 * @returns Credentials or null
 */
export function getCredentials(): { email: string; password: string } | null {
  try {
    const credentials = localStorage.getItem(CREDENTIALS_KEY)
    if (!credentials) {
      return null
    }
    return JSON.parse(credentials)
  } catch (error) {
    console.error('Error parsing credentials from localStorage:', error)
    localStorage.removeItem(CREDENTIALS_KEY)
    return null
  }
}

/**
 * Retrieves stored user data
 * @returns User object or null
 */
export function getUser(): LoginResponse['user'] | null {
  try {
    const userString = localStorage.getItem(USER_KEY)
    if (!userString) {
      return null
    }
    return JSON.parse(userString)
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error)
    localStorage.removeItem(USER_KEY)
    return null
  }
}

/**
 * Creates axios instance with auth token
 * @returns Axios instance
 */
function createApiClient() {
  const token = getToken()
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  })
}

/**
 * Logs in user with email and password
 * @param credentials - Login credentials
 * @returns Promise with login response
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.post('/auth/login', credentials)
    const data: LoginResponse = response.data
    
    storeToken(data.token)
    storeUser(data.user)
    storeCredentials(credentials.email, credentials.password)
    
    return data
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.loginFailed'))
  }
}

/**
 * Logs out current user
 * @returns void
 */
export function logout(): void {
  removeToken()
}

/**
 * Gets current user info from API
 * @returns Promise with user object
 */
export async function getCurrentUser(): Promise<LoginResponse['user']> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.get('/auth/me')
    const user = response.data
    storeUser(user)
    return user
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.userInfoFailed'))
  }
}

/**
 * Refreshes JWT token
 * @returns Promise with new token
 */
export async function refreshToken(): Promise<string> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.post('/auth/refresh')
    const { token } = response.data
    storeToken(token)
    return token
  } catch (error: any) {
    removeToken()
    throw new Error('Token refresh failed')
  }
}

/**
 * Requests a password reset email
 * @param email - User email address
 * @returns Promise with message
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.post('/auth/forgot-password', { email })
    return response.data
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.resetFailed'))
  }
}

/**
 * Resets password using token
 * @param token - Reset token
 * @param password - New password
 * @returns Promise with message
 */
export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.post('/auth/reset-password', { token, password })
    return response.data
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.resetPasswordFailed'))
  }
}

/**
 * Gets invitation info for the accept-invitation screen
 * @param token - Invitation token
 * @returns Promise with invitation info
 */
export async function getInvitationInfo(token: string): Promise<{ email: string; name: string; accountName: string }> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.get(`/auth/invitation-info?token=${encodeURIComponent(token)}`)
    return response.data
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.invitationExpired'))
  }
}

/**
 * Accepts an invitation and sets password
 * @param token - Invitation token
 * @param password - New password
 * @returns Promise with message
 */
export async function acceptInvitation(token: string, password: string): Promise<{ message: string }> {
  try {
    const apiClient = createApiClient()
    const response = await apiClient.post('/auth/accept-invitation', { token, password })
    return response.data
  } catch (error: any) {
    throw new Error(translateAuthError(error, 'auth.acceptInvitationFailed'))
  }
}
